import paths from 'paths'
import React, { useEffect, useMemo, useState } from 'react'

import { TENANT_LOCAL_STORAGE_KEY } from '.'
import TenantContext from './context'

type Props = {
  children: React.ReactNode
}

/** Get the initial tenant from local storage. */
function initialTenant(): string | null {
  return localStorage.getItem(TENANT_LOCAL_STORAGE_KEY) || null
}

/** Get the tenant home path. */
function getTenantHome(tenant: string | null) {
  if (!tenant) {
    return paths.gettingStarted()
  } else {
    return paths.organizationManagement({ slug: tenant })
  }
}

/** Tenant context provider. On mount, it will check for a tenant in local storage and
 *  set it as the current tenant. It will also update local storage whenever the
 *  tenant is updated. This persistence was added so the /support page can be refreshed
 *  without losing the tenant context.
 */
export default function TenantContextProvider({ children }: Props) {
  const [currentTenant, setCurrentTenant] = useState<string | null>(
    initialTenant(),
  )

  useEffect(() => {
    if (currentTenant) {
      localStorage.setItem(TENANT_LOCAL_STORAGE_KEY, currentTenant)
    } else {
      localStorage.removeItem(TENANT_LOCAL_STORAGE_KEY)
    }
  }, [currentTenant])

  const tenantHome = useMemo(
    () => getTenantHome(currentTenant),
    [currentTenant],
  )

  return (
    <TenantContext.Provider
      value={{
        tenant: currentTenant,
        updateTenant: setCurrentTenant,
        getTenantHome,
        tenantHome,
      }}
    >
      {children}
    </TenantContext.Provider>
  )
}
